import {
  Badge,
  Box,
  Button,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@elkaso-app/web-design';
import { useGetSupplierContactHistoryModalApi } from 'apis/crm-supplier-contact-quotas';
import React, { useCallback, useState } from 'react';

interface ContactHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplierId: number;
  supplierName: string;
}

export const ContactHistoryModal: React.FC<ContactHistoryModalProps> = ({
  isOpen,
  onClose,
  supplierId,
  supplierName,
}) => {
  // Pagination state - completely independent from global URL params
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(20); // Fixed at 20 items per page as requested

  // Reset pagination when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
    }
  }, [isOpen, supplierId]); // Also reset when supplier changes

  // Create independent API call with manual page/limit parameters
  const {
    data: historyData,
    isLoading,
    isFetching,
  } = useGetSupplierContactHistoryModalApi(supplierId, currentPage, limit, {
    enabled: isOpen && !!supplierId,
    // Override the global page params with our local state
    cacheTime: 5 * 60 * 1000, // 5 minutes
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Calculate pagination for server-side pagination from API
  const currentData = historyData?.data || [];
  const totalPages = historyData?.pageCount || 0;
  const totalContacts = historyData?.total || 0;
  const currentPageNum = historyData?.page || currentPage;
  const currentLimit = historyData?.limit || limit;

  // Simple pagination handlers (after totalPages is defined)
  const handlePageClick = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePrevPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, totalPages]);

  // Calculate display range for "Showing X-Y of Z"
  const startIndex = totalContacts > 0 ? (currentPageNum - 1) * currentLimit + 1 : 0;
  const endIndex = Math.min(currentPageNum * currentLimit, totalContacts);
  const showingText =
    totalContacts > 0 ? `Showing ${startIndex}-${endIndex} of ${totalContacts} contacts` : 'No contacts to display';

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='6xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Contact History - {supplierName}
          <Text fontSize='sm' fontWeight='normal' color='gray.600' mt={1}>
            Supplier ID: {supplierId}
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {isLoading ? (
            <Box textAlign='center' py={8}>
              <Spinner size='lg' />
              <Text mt={4} color='gray.600'>
                Loading contact history...
              </Text>
            </Box>
          ) : historyData?.data && historyData.data.length > 0 ? (
            <VStack spacing={4} align='stretch'>
              {/* Summary Stats */}
              <HStack spacing={4} justify='space-between'>
                <Box p={4} bg='blue.50' borderRadius='md' border='1px solid' borderColor='blue.200' flex={1}>
                  <Text fontSize='sm' color='blue.800' textAlign='center'>
                    <strong>Total Contacts Revealed:</strong> {totalContacts} contacts
                  </Text>
                </Box>
                <Box p={4} bg='green.50' borderRadius='md' border='1px solid' borderColor='green.200'>
                  <Text fontSize='sm' color='green.800' textAlign='center'>
                    <strong>Page:</strong> {currentPageNum} of {totalPages}
                  </Text>
                </Box>
              </HStack>

              {/* Loading Spinner for Pagination */}
              {isFetching && (
                <Box textAlign='center' py={2}>
                  <Spinner size='sm' />
                  <Text fontSize='sm' color='gray.600' ml={2} display='inline'>
                    Updating...
                  </Text>
                </Box>
              )}

              {/* Data Table */}
              <TableContainer>
                <Table variant='simple' size='sm'>
                  <Thead bg='gray.50'>
                    <Tr>
                      <Th>Contact & Position</Th>
                      <Th>Brand</Th>
                      <Th>Contact Details</Th>
                      <Th>Revealed Date</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {currentData.map((contact) => (
                      <Tr key={contact.id}>
                        {/* Contact Name & Position Combined */}
                        <Td>
                          <VStack align='start' spacing={1}>
                            <Text fontWeight='medium' color='blue.600' fontSize='sm'>
                              {contact.brandContactDetail?.name || 'N/A'}
                            </Text>
                            <Badge colorScheme='purple' variant='subtle' size='xs'>
                              {contact.brandContactDetail?.position || 'Unknown Position'}
                            </Badge>
                          </VStack>
                        </Td>

                        {/* Brand */}
                        <Td>
                          <Badge colorScheme='blue' variant='subtle' size='sm'>
                            {contact.brandContactDetail?.brand?.name || 'Unknown Brand'}
                          </Badge>
                        </Td>

                        {/* Email & Phone Combined */}
                        <Td>
                          <VStack align='start' spacing={1}>
                            {contact.brandContactDetail?.email && (
                              <HStack spacing={1}>
                                <Text fontSize='xs' color='gray.500'>
                                  📧
                                </Text>
                                <Text fontSize='sm' color='gray.700'>
                                  {contact.brandContactDetail.email}
                                </Text>
                              </HStack>
                            )}
                            {contact.brandContactDetail?.phone && (
                              <HStack spacing={1}>
                                <Text fontSize='xs' color='gray.500'>
                                  📞
                                </Text>
                                <Text fontSize='sm' color='gray.700'>
                                  {contact.brandContactDetail.phone}
                                </Text>
                              </HStack>
                            )}
                            {!contact.brandContactDetail?.email && !contact.brandContactDetail?.phone && (
                              <Text fontSize='sm' color='gray.500' fontStyle='italic'>
                                No contact info
                              </Text>
                            )}
                          </VStack>
                        </Td>

                        {/* Revealed Date */}
                        <Td>
                          <Text fontSize='sm' color='gray.600'>
                            {new Date(contact.revealedAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Text>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <Box borderTop='1px solid' borderColor='gray.200' pt={4}>
                  <HStack justify='space-between' align='center'>
                    <Text fontSize='sm' color='gray.600'>
                      {showingText}
                    </Text>

                    {/* Custom Pagination Buttons */}
                    <HStack spacing={2}>
                      {/* Previous Button */}
                      <IconButton
                        aria-label='Previous page'
                        icon={<Text fontSize='sm'>←</Text>}
                        size='sm'
                        variant='outline'
                        isDisabled={currentPage <= 1}
                        onClick={handlePrevPage}
                      />

                      {/* Page Numbers */}
                      {Array.from({ length: totalPages }, (_, index) => {
                        const pageNum = index + 1;
                        const isCurrentPage = pageNum === currentPage;

                        // Show first page, last page, current page, and pages around current
                        const showPage =
                          pageNum === 1 || pageNum === totalPages || Math.abs(pageNum - currentPage) <= 1;

                        if (!showPage) {
                          // Show ellipsis for gaps
                          if (pageNum === 2 && currentPage > 4) {
                            return (
                              <Text key={`ellipsis-${pageNum}`} fontSize='sm' color='gray.500' px={1}>
                                ...
                              </Text>
                            );
                          }
                          if (pageNum === totalPages - 1 && currentPage < totalPages - 3) {
                            return (
                              <Text key={`ellipsis-${pageNum}`} fontSize='sm' color='gray.500' px={1}>
                                ...
                              </Text>
                            );
                          }
                          return null;
                        }

                        return (
                          <IconButton
                            key={pageNum}
                            aria-label={`Page ${pageNum}`}
                            variant={isCurrentPage ? 'solid' : 'outline'}
                            colorScheme={isCurrentPage ? 'blue' : 'gray'}
                            size='sm'
                            onClick={() => handlePageClick(pageNum)}>
                            <Text fontSize='sm'>{pageNum}</Text>
                          </IconButton>
                        );
                      })}

                      {/* Next Button */}
                      <IconButton
                        aria-label='Next page'
                        icon={<Text fontSize='sm'>→</Text>}
                        size='sm'
                        variant='outline'
                        isDisabled={currentPage >= totalPages}
                        onClick={handleNextPage}
                      />
                    </HStack>
                  </HStack>
                </Box>
              )}
            </VStack>
          ) : (
            <Box textAlign='center' py={8}>
              <Text color='gray.600'>No contact history found for this supplier.</Text>
              <Text fontSize='sm' color='gray.500' mt={2}>
                This supplier hasn&apos;t revealed any contacts yet.
              </Text>
            </Box>
          )}
        </ModalBody>

        <ModalFooter>
          <Button colorScheme='blue' onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
